<template>
  <Modal
    size="xl"
    @close="$emit('close')"
  >
    <template #header>
      <div class="w-full text-center">
        <h1 class="text-2xl">
          {{ $t('HeadContract.DelaysSchedule.DelayCorrespondenceModal.title') }}
        </h1>
      </div>
    </template>

    <template #body>
      <div class="flex flex-col h-full w-full overflow-y-auto">
        <div v-if="fetchCorrespondencesAction.is(States.COMPLETE)">
          <div class="flex justify-between items-center">
            <div class="font-semibold text-lg w-1/2 text-wrap">
              {{ delay.code ? delay.code + ' - ' : '' }} {{ delay.description }}
            </div>

            <div
              v-if="projectsStore.project?.unarchived"
              class="flex space-x-4 items-center"
            >
              <button
                type="button"
                class="btn btn--secondary-blue"
                @click="showForm = !showForm"
              >
                {{ showForm ? $t('HeadContract.DelaysSchedule.DelayCorrespondenceModal.button.showCorrespondence') : $t('HeadContract.DelaysSchedule.DelayCorrespondenceModal.button.addCorrespondence') }}
              </button>
            </div>
          </div>

          <div
            v-if="showForm"
            class="flex justify-center"
          >
            <form
              action=""
              class="flex flex-col space-y-4 p-2 w-2/3 mx-auto"
              @submit.prevent="saveCorrespondence"
            >
              <label>
                {{ $t('HeadContract.DelaysSchedule.DelayCorrespondenceModal.label.dateReceived') }}

                <AppInput
                  v-model="correspondenceForm.form.date_received"
                  name="date_received"
                  type="date"
                  class="form-input"
                  :error="correspondenceForm.getErrors('date_received')"
                />
              </label>

              <label>
                {{ $t('HeadContract.DelaysSchedule.DelayCorrespondenceModal.label.to') }}

                <AppInput
                  v-model="correspondenceForm.form.to"
                  name="to"
                  type="text"
                  class="form-input"
                  :error="correspondenceForm.getErrors('to')"
                />
              </label>

              <label>
                {{ $t('HeadContract.DelaysSchedule.DelayCorrespondenceModal.label.from') }}

                <AppInput
                  v-model="correspondenceForm.form.from"
                  name="from"
                  class="form-input"
                  type="text"
                  :error="correspondenceForm.getErrors('from')"
                />
              </label>

              <label>
                {{ $t('HeadContract.DelaysSchedule.DelayCorrespondenceModal.label.description') }}

                <textarea
                  v-model="correspondenceForm.form.description"
                  name="description"
                  class="form-input resize-none"
                  type="text"
                />

                <span
                  v-if="correspondenceForm.getErrors('description')"
                  class="text-red-600 text-sm"
                >{{ correspondenceForm.getErrors('description') }}</span>
              </label>

              <label class="flex justify-between w-full">
                {{ $t('HeadContract.DelaysSchedule.DelayCorrespondenceModal.label.responseRequired') }}

                <input
                  v-model="correspondenceForm.form.response_required"
                  name="response_required"
                  type="checkbox"
                  class="form-checkbox"
                  true-value="1"
                  false-value="0"
                >
              </label>

              <div>
                {{ $t('HeadContract.DelaysSchedule.DelayCorrespondenceModal.label.correspondenceWas') }}
                <div class="flex items-center mx-auto space-x-4">
                  <button
                    type="button"
                    class="btn w-1/2"
                    :class="[correspondenceForm.form.is_issued === 0 ? 'btn--primary-blue' : 'btn--secondary-blue']"
                    data-test="type-internal"
                    @click="correspondenceForm.form.is_issued = 0"
                  >
                    {{ $t('HeadContract.DelaysSchedule.DelayCorrespondenceModal.button.received') }}
                  </button>

                  <button
                    type="button"
                    class="btn w-1/2"
                    :class="[correspondenceForm.form.is_issued === 1 ? 'btn--primary-blue' : 'btn--secondary-blue']"
                    data-test="type-external"
                    @click="correspondenceForm.form.is_issued = 1"
                  >
                    {{ $t('HeadContract.DelaysSchedule.DelayCorrespondenceModal.button.issued') }}
                  </button>
                </div>
              </div>

              <div
                class="h-20 w-full"
              >
                <FileUpload
                  v-model="correspondenceForm.form.media"
                  multiple
                  @change="addFile"
                />
              </div>

              <div
                v-if="filesUploaded.length"
                class="overflow-x-auto flex"
              >
                <UploadedFileDisplay
                  v-for="(file, i) in filesUploaded"
                  :key="`${file.fileName}-${i}`"
                  :file="file"
                  :error="correspondenceForm.getErrors(`media.${i}`)"
                  @delete="removeFile(i)"
                />
              </div>
            </form>
          </div>

          <div
            v-else
            class=""
          >
            <table
              v-if="correspondences.length > 0"
              class="w-full mt-4"
            >
              <thead>
                <tr class="text-left child:p-2 child:border-y first-child:border-l last-child:border-r child:border-gray-300">
                  <th>
                    <div>
                      {{ $t('HeadContract.DelaysSchedule.DelayCorrespondenceModal.table.description') }}

                      <button
                        type="button"
                        class="text-gray-300 text-sm"
                        @click="sortColumn = sortColumn === 'type' ? 'date_received' : 'type'"
                      >
                        sort by {{ sortColumn === 'type' ? 'date' : 'type' }}
                      </button>
                    </div>
                  </th>

                  <th>
                    {{ $t('HeadContract.DelaysSchedule.DelayCorrespondenceModal.table.to') }}
                  </th>

                  <th>
                    {{ $t('HeadContract.DelaysSchedule.DelayCorrespondenceModal.table.from') }}
                  </th>

                  <th>
                    {{ $t('HeadContract.DelaysSchedule.DelayCorrespondenceModal.table.issuedReceived') }}
                  </th>

                  <th>
                    {{ $t('HeadContract.DelaysSchedule.DelayCorrespondenceModal.table.date') }}
                  </th>

                  <th>
                    {{ $t('HeadContract.DelaysSchedule.DelayCorrespondenceModal.table.responseRequired') }}
                  </th>
                </tr>
              </thead>

              <tbody>
                <CorrespondenceRow
                  v-for="correspondence in correspondences"
                  :key="correspondence.id"
                  :correspondence="correspondence"
                />
              </tbody>
            </table>

            <p
              v-else
              class="font-semibold text-center m-4"
            >
              {{ $t('HeadContract.DelaysSchedule.DelayCorrespondenceModal.noCorrespondencesFound') }}
            </p>
          </div>
        </div>

        <AppSpinner
          v-else
          class="mx-auto"
        />
      </div>
    </template>

    <template #footer>
      <div class="w-full flex justify-end p-2 space-x-4">
        <button
          type="button"
          class="btn btn--secondary-blue"
          @click="$emit('close');"
        >
          {{ $t('HeadContract.DelaysSchedule.DelayCorrespondenceModal.button.close') }}
        </button>

        <button
          v-if="showForm"
          type="button"
          class="btn btn--primary-blue"
          @click="saveCorrespondence"
        >
          {{ $t('HeadContract.DelaysSchedule.DelayCorrespondenceModal.button.save') }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import FileUpload from '@/components/FileUpload.vue';
import Modal from '@/components/Modal.vue';
import UploadedFileDisplay from '@/components/UploadedFileDisplay.vue';
import { useFileUploadForm } from '@/composables/useFileUploadForm';
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { dateForDateInput, falsey } from '@/helpers';
import { DelayCorrespondenceTypes } from '@/models/Delay';
import { useDelaysStore } from '@/store/delays';
import { useProjectsStore } from '@/store/projects';
import Swal from 'sweetalert2';
import { computed, ref, watch } from 'vue';
import CorrespondenceRow from './CorrespondenceRow.vue';

const props = defineProps<{
  delayId: Id;
}>();

const emits = defineEmits<{
  (e: 'close');
}>();

const delaysStore = useDelaysStore();
const sortColumn = ref<'type' | 'date_received'>('type');

const delay = computed(() => {
  return delaysStore.models
    .with('user')
    .with('correspondences', (query) => {
      query.orderBy('date_received', 'desc');
    })
    .where(
      'id',
      parseInt(props.delayId as string),
    )
    .first();
});

const correspondences = computed(() => {
  let delayCorrespondences = [];

  switch (sortColumn.value) {
    case 'type':
      delayCorrespondences = delay.value.correspondences.toSorted((a, b) => {
        if(!falsey(a.type) && !falsey(b.type)) {
          return (DelayCorrespondenceTypes[b.type] ?? 0) - (DelayCorrespondenceTypes[a.type] ?? 0);
        } else if(falsey(a.type) && !falsey(b.type)) {
          return 1;
        }

        return -1;
      });

      break;

    case 'date_received':
      delayCorrespondences = delay.value.correspondences;

      break;
  }

  return delayCorrespondences;
});

const fetchCorrespondencesAction = useStoreApiAction(delaysStore.fetchCorrespondences);
const storeCorrespondenceAction = useStoreApiAction(delaysStore.storeCorrespondence);

watch(() => props.delayId, (newValue) => {
  if(newValue) {
    fetchCorrespondencesAction.request(newValue);
  }
}, { immediate: true });

const correspondenceForm = useFormData({
  date_received: dateForDateInput(new Date()),
  to: '',
  from: '',
  description: '',
  response_required: 0,
  is_issued: 0,
  media: [],
});

const showForm = ref(false);

const { filesUploaded, addFile, removeFile } = useFileUploadForm(undefined, (file, index) => {
  correspondenceForm.form.media.splice(index, 1);
});

const saveCorrespondence = () => {
  storeCorrespondenceAction.request(props.delayId, correspondenceForm.toFormDataObject()).then(() => {
    Swal.fire({
      icon: 'success',
      text: 'Correspondence Saved',
    }).then(() => {
      emits('close');
    });

    correspondenceForm.resetData();
    filesUploaded.value = [];
  }).catch((error) => {
    correspondenceForm.setErrors(error.data);
  });
};

const projectsStore = useProjectsStore();
</script>

<style scoped></style>
